import { cloneDeep, forEach, groupBy, unionBy } from 'lodash';
import { dayjs } from '@/extendedDayjs';
import { createSummaryRequestId } from '@/selectors/summaryViewSelectors';
import { appGroupsMock } from '@/testData/appGroups';
import { mockCrossPlatformAppSettings, mockWhiteLabelAppSettings } from '@/testData/appSettings';
import { fillStepsWithReleaseData } from '@/testData/baseRelease';
import { featuresStep, getCompactDataFromFeaturesStep } from '@/testData/featureItems';
import { injectAppStoreData, mockReleaseForPlatform, mockReviewSubmissionAppStoreData } from '@/testData/liveRelease';
import { mockOrgUsers } from '@/testData/orgUsers';
import { mockPhasedReleaseForPlatform } from '@/testData/phasedRelease';
import { generateReleaseCalendarDemoData, generateReleaseCalendarForWorkflowRun } from '@/testData/releaseCalendar';
import { baseReleaseStepsForPlatform, getMockedStatusReasonByArchetypeAndStatus } from '@/testData/releaseSteps';
import { mockCrossPlatformAppReleases, mockWhiteLabelAppReleases } from '@/testData/releases';
import { mockStepConfigForArchetype } from '@/testData/stepConfigs';
import { convertAppHomeToSummaryView, convertWorkflowOverviewToSummaryView } from '@/testData/summaryView';
import { upcomingReleaseMock } from '@/testData/upcomingRelease';
import { mockWorkflowOverview } from '@/testData/workflowOverview';
import { buildStepConfigToDestinationsMap } from '@/testData/workflows';
import type { Destination } from '@/types/destinations';
import type { StepConfig } from '@/types/stepConfig';
import type { StepRun } from '@/types/stepRun';
import type {
    CIDistributionStepRunStatusData,
    ReleaseStepRunStatusData,
    StepRunStatusData,
    VCSDistributionStepRunStatusData,
} from '@/types/stepRunStatus';
import {
    type OrgDestinationsResponse,
    type OrgSummaryPreferences,
    PageViewType,
    type SummaryViewResponseAPI,
    TabularLayoutType,
    type VersionSummaryViewDataItemAPI,
    AppDestinationsAndVersions,
} from '@/types/summaryView';
import type { WorkflowStepGraph } from '@/types/workflow';
import type { WorkflowRunStepRunGraph } from '@/types/workflowRunStepRunGraph';
import type { StringKeyedDictionary } from '@/typescriptHelpers';
import type { AppDemoData, DemoAppsPayload, DemoReleasePayload } from '../actions/ui';
import {
    computeNextVersionFromVersion,
    computePreviousVersionFromVersion,
    getIsApplePlatform,
    getIsOTAPlatform,
    getLifecycleAndReleaseCycleEvents,
    isAndroidPlatform,
    isAppPlatform,
    isIosPlatform,
} from './../helpers';
import { legacyAppMock, mockWorkflowsApp } from '../testData/app';
import { appHomeMock } from '../testData/appHome';
import { androidAutomations, iosAutomations, otaAutomations } from '../testData/automations';
import { generateBuildDistributionDemoData, type RecursiveStringifyEnums } from '../testData/buildDistro';
import { generateFeatureFlags } from '../testData/featureFlags';
import { mockedIntegrationProviders } from '../testData/integrationProviders';
import { androidIntegrations, iosIntegrations, otaIntegrations } from '../testData/integrations';
import { generateOrgOverviewStats } from '../testData/orgOverview';
import { generateReleaseOverview, generateReleaseStatsHistory } from '../testData/releaseStatsHistory';
import { generateRollbackSteps, rollbackReleaseMock } from '../testData/rollbackRelease';
import { baseRolloutData } from './../testData/rollout';
import {
    androidStepAutomations,
    iosStepAutomations,
    otaStepAutomations,
    workflowOnlyStepAutomations,
} from '../testData/stepAutomations';
import { generateTimelineEventsHistory } from './../testData/timelineEventsHistory';
import {
    type AppEntity,
    type AppHome,
    AppleAppStoreState,
    AppMonitoringHealthMetricCategory,
    type AppMonitoringHealthMetricType,
    AppPlatform,
    type AppSettings,
    type AppStoreAvailableBuild,
    AppStoreReviewSubmissionItemState,
    AppStoreReviewSubmissionState,
    AppStoreType,
    type Automation,
    type AutomationRun,
    AutomationStatus,
    AutomationType,
    BuildArtifactType,
    type ChecklistItem,
    type CodeItem,
    type FeatureAffiliationType,
    type FeatureFlag,
    type FeatureItem,
    FeatureItemDebugVariant,
    GooglePlayStoreState,
    type Integration,
    type IntegrationProvider,
    IntegrationType,
    type OrgUser,
    type Release,
    type ReleaseCalendarData,
    ReleaseStatus,
    type ReleaseStep,
    type ReleaseStepBetaTestingData,
    ReleaseStepBlockingConfig,
    type ReleaseStepCIDistributionData,
    type ReleaseStepKickoff,
    type ReleaseStepKickoffData,
    type ReleaseStepReleaseCandidateData,
    type ReleaseStepRollbackBuild,
    ReleaseStepStatus,
    type ReleaseStepStoreReviewData,
    type ReleaseStepSubmissionData,
    type ReleaseStepTakeoffData,
    ReleaseStepType,
    type ReleaseStepVCSDistributionData,
    ReleaseTagState,
    ReleaseTimelinePhase,
    type ReleaseType,
    type RolesMap,
    type RolloutMonitoring,
    type UpcomingReleaseSummary,
    type User,
    UserRatingsMetricType,
} from './../types';
import { displayTextForReleaseStepType } from './displayHelpers';

export const massageTestReleaseData: (
    appPlatform: AppPlatform,
    testRelease: Release,
    releaseSteps: ReleaseStep[],
    appUsesStaticReleaseBranchForNonHotfixes: boolean,
    featureFlags: FeatureFlag[],
    defaultReleaseType: ReleaseType,
    currentRelease?: Release,
    currentReleaseSteps?: ReleaseStep[],
    releasePilotId?: string,
    currentUser?: User,
) => { testRelease: Release; testReleaseSteps: ReleaseStep[] } = (
    appPlatform,
    testRelease,
    releaseSteps,
    appUsesStaticReleaseBranchForNonHotfixes,
    featureFlags,
    defaultReleaseType,
    currentRelease,
    currentReleaseSteps,
    releasePilotId,
    currentUser,
) => {
    const releaseVersion = currentRelease?.version ?? testRelease.version;
    const isComplete = (currentRelease ?? testRelease).status === ReleaseStatus.Completed;
    const displayableVersion = currentRelease?.displayableVersion ?? testRelease.displayableVersion;
    const displayableNextVersion = currentRelease?.displayableNextVersion ?? testRelease.displayableNextVersion;
    const releaseNextVersion =
        currentRelease?.nextVersion ?? computeNextVersionFromVersion(releaseVersion, defaultReleaseType);

    const defaultReleaseBranchForPlatform =
        getIsOTAPlatform(appPlatform) ? `codepush-${releaseVersion}` : `release-${releaseVersion}`;

    const kickoffStep = releaseSteps.find((step) => step.type === ReleaseStepType.Kickoff)!;

    const doesCurrentReleaseExistAndBranchIsPopulated =
        kickoffStep?.data.releaseBranch && kickoffStep?.data.releaseBranch !== '';
    const releaseBranch =
        doesCurrentReleaseExistAndBranchIsPopulated ? kickoffStep.data?.releaseBranch
        : appUsesStaticReleaseBranchForNonHotfixes ? 'prod'
        : defaultReleaseBranchForPlatform;
    const isStaticBranch =
        doesCurrentReleaseExistAndBranchIsPopulated ?
            currentRelease?.isStaticReleaseBranch == true
        :   appUsesStaticReleaseBranchForNonHotfixes;
    const workingBranch =
        kickoffStep.data?.workingBranch && kickoffStep.data?.workingBranch !== '' ?
            kickoffStep?.data.workingBranch
        :   'develop';

    testRelease.id = currentRelease?.id ?? testRelease.id;
    testRelease.releaseId = currentRelease?.releaseId ?? testRelease.releaseId;
    testRelease.version = releaseVersion;
    testRelease.nextVersion = releaseNextVersion;
    testRelease.displayableVersion = displayableVersion;
    testRelease.displayableNextVersion = displayableNextVersion;
    testRelease.releasePilotId = releasePilotId;
    const targetKickoffDate = dayjs().subtract(1, 'day').toISOString();
    testRelease.targetKickoffDate = targetKickoffDate;
    testRelease.featureFlags = featureFlags;

    const appStoreType =
        getIsApplePlatform(appPlatform) ? AppStoreType.Apple
        : getIsOTAPlatform(appPlatform) ? undefined
        : AppStoreType.Google;

    testRelease.timelineEventsHistory = generateTimelineEventsHistory({
        currentReleaseId: currentRelease?.id,
        version: releaseVersion,
        currentUser,
        appStoreType,
        platform: appPlatform,
        isReleaseComplete: isComplete,
    });

    const targetSubmitDate = dayjs().add(2, 'days').toISOString();
    const targetReleaseDate = dayjs().add(4, 'days').toISOString();

    testRelease.releaseOverview = generateReleaseOverview({
        targetKickoffDate,
        targetReleaseDate,
        targetSubmitDate,
        kickOffDate: targetKickoffDate,
        submitDate: targetSubmitDate,
    });
    testRelease.kickedOffAt = targetKickoffDate;
    testRelease.targetSubmitDate = targetSubmitDate;
    testRelease.releaseStatsHistory = generateReleaseStatsHistory({
        version: releaseVersion,
        platform: appPlatform,
        isCompletedRelease: isComplete,
    });

    testRelease.targetSubmitDate = targetSubmitDate;
    testRelease.targetReleaseDate = targetReleaseDate;

    const kickoffAutomation = kickoffStep.automationRuns.find(
        (automation) => automation.type === AutomationType.AutoKickoffRelease,
    );

    // customize kickoff automation description to accommodate branching strategy
    if (kickoffAutomation) {
        kickoffAutomation.description =
            isStaticBranch ?
                `Runway will promote your working branch '${workingBranch}' to your release branch '${releaseBranch}'.`
            :   `Runway will create a release branch for version ${releaseVersion} off of ${workingBranch}.`;
    }

    const kickoffData = kickoffStep?.data as ReleaseStepKickoffData;
    const currentReleaseKickoffData = currentReleaseSteps?.find((step) => step.type === ReleaseStepType.Kickoff)
        ?.data as ReleaseStepKickoffData;

    kickoffData.releaseBranch = releaseBranch;
    kickoffData.isStaticReleaseBranch = isStaticBranch;
    kickoffData.releaseBranchCodeVersionData!.branchName = releaseBranch;
    kickoffData.releaseBranchCodeVersionData!.currentCodeVersion = releaseVersion;
    kickoffData.workingBranchCodeVersionData!.currentCodeVersion = releaseVersion;
    kickoffData.workingBranchCodeVersionData!.targetCodeVersion = releaseVersion; // since already bumped
    kickoffData.workingBranchCodeVersionData!.branchName = workingBranch;
    kickoffData.resolvedFeatureAffiliations = kickoffData.resolvedFeatureAffiliations.map((fa) => {
        return {
            ...fa,
            resolvedString: getIsOTAPlatform(appPlatform) ? `codepush-${releaseVersion}` : `release-${releaseVersion}`,
        };
    });

    const computedPrevVersion = computePreviousVersionFromVersion(releaseVersion);

    kickoffData.previousReleaseVersion =
        currentReleaseKickoffData?.previousReleaseVersion ?? computedPrevVersion ?? undefined;
    kickoffData.previousReleaseTag =
        (currentReleaseKickoffData?.previousReleaseTag ?? kickoffData.previousReleaseVersion) ?
            `v${kickoffData.previousReleaseVersion}`
        :   undefined;
    kickoffData.previousTaggedReleaseTag =
        (currentReleaseKickoffData?.previousTaggedReleaseTag ?? kickoffData.previousReleaseVersion) ?
            `v${kickoffData.previousReleaseVersion}`
        :   undefined;
    kickoffData.previousTaggedReleaseTagVersionsBehind = 0;

    const rcData = releaseSteps.find((step) => step.type === ReleaseStepType.ReleaseCandidate)
        ?.data as ReleaseStepReleaseCandidateData;

    rcData.workflowData!.branch = releaseBranch;

    if (isAppPlatform(appPlatform)) {
        const submissionStep = releaseSteps.find((step) => step.type === ReleaseStepType.Submission) as ReleaseStep;
        const submitAutomation = submissionStep.automationRuns.find(
            (automation) => automation.type === AutomationType.AutoSubmitRelease,
        );
        submitAutomation!.scheduledAt = targetSubmitDate;
    }

    const betaStepData = releaseSteps.find((step) => step.type === ReleaseStepType.BetaTesting)
        ?.data as ReleaseStepBetaTestingData;
    betaStepData.soakStartedAt = dayjs().subtract(15, 'hours').toISOString();

    if (isIosPlatform(appPlatform)) {
        const releaseStep = releaseSteps.find((step) => step.type === ReleaseStepType.Takeoff) as ReleaseStep;
        const releaseAutomation = releaseStep.automationRuns.find(
            (automation) => automation.type === AutomationType.AutoRelease,
        );
        releaseAutomation!.scheduledAt = targetReleaseDate;
    }

    return { testRelease, testReleaseSteps: releaseSteps };
};

export const massageFeatureItem: (
    item: FeatureItem,
    faIntegration: IntegrationType,
    faType: FeatureAffiliationType,
    releaseVersion: string,
    baseBranch: string,
    platform: AppPlatform,
) => FeatureItem = (item, faIntegration, faType, releaseVersion, baseBranch, platform) => {
    //clone old item
    let code = item.code;
    if (
        item.code &&
        [
            FeatureItemDebugVariant.CodeMergedTicket,
            FeatureItemDebugVariant.CodeMergedNoTicket,
            FeatureItemDebugVariant.CodeUnmergedTicket,
            FeatureItemDebugVariant.OpenPRNoTicket,
            FeatureItemDebugVariant.OpenPRTicket,
        ].includes(item.debugVariant)
    ) {
        if (code as CodeItem) {
            code = {
                ...code,
                branch: baseBranch,
            } as typeof code;
        } else {
            code = {
                ...code,
                baseBranch: baseBranch,
            } as typeof code;
        }
    }

    let ticket = item.ticket;

    const resolvedString = getIsOTAPlatform(platform) ? `codepush-${releaseVersion}` : `release-${releaseVersion}`;
    if (
        item.ticket?.featureAffiliations &&
        !item.ticket?.featureAffiliations.some((fa) => fa?.resolvedString === resolvedString)
    ) {
        ticket = {
            ...item.ticket,
            featureAffiliations: [
                ...item.ticket.featureAffiliations,
                {
                    resolvedString,
                    integrationId: faIntegration,
                    affiliationType: faType,
                },
            ],
        };
    }

    return {
        ...item,
        code,
        ticket,
    };
};

export const massageAppHomeData: (
    platform: AppPlatform,
    appHome: AppHome,
    liveReleaseId: string,
    liveReleaseVersion: string,
    nextReleaseId: string,
    nextReleaseVersion: string,
    upcomingReleaseId: string,
    upcomingReleaseVersion: string,
    rolloutMonitoring: RolloutMonitoring,
) => AppHome = (
    platform,
    appHome,
    liveReleaseId,
    liveVersion,
    nextReleaseId,
    nextReleaseVersion,
    upcomingReleaseId,
    upcomingReleaseVersion,
    rolloutMonitoring,
) => {
    appHome.liveRelease!.rolloutMonitoring = rolloutMonitoring;

    const isOTAPlatform = getIsOTAPlatform(platform);

    appHome.liveRelease!.displayableVersion = liveVersion;
    appHome.liveRelease!.version = liveVersion;
    appHome.liveRelease!.id = liveReleaseId;
    appHome.liveRelease!.releaseId = liveReleaseId;
    appHome.liveRelease!.completedAt = dayjs().subtract(2, 'day').toISOString();

    if (isAndroidPlatform(platform)) {
        appHome.liveRelease!.appStoreState = GooglePlayStoreState.Completed;
        appHome.liveRelease!.appStoreBuilds = appHome.liveRelease!.appStoreBuilds.map((build) => {
            return {
                ...build,
                appStoreBuild: {
                    ...build.appStoreBuild,
                    integrationId: IntegrationType.Google,
                    buildNumber: '56',
                },
                associatedCIBuild: {
                    ...build.associatedCIBuild,
                    buildIdentifier: '56',
                },
            };
        }) as AppStoreAvailableBuild[];

        appHome.nextRelease!.percentFeatureComplete = 60;
    } else if (isOTAPlatform) {
        appHome.liveRelease!.appStoreState = undefined;
        appHome.liveRelease!.appStoreBuilds = [];
        appHome.liveRelease!.appStoreReviewData = undefined;

        appHome.nextRelease!.percentFeatureComplete = 20;
    }

    appHome.nextRelease!.targetReleaseDate = dayjs().add(3, 'day').toISOString();
    appHome.nextRelease!.kickedOffAt = dayjs().subtract(1, 'day').toISOString();
    appHome.nextRelease!.targetKickoffDate = dayjs().subtract(1, 'day').toISOString();
    appHome.nextRelease!.targetSubmitDate = dayjs().add(2, 'days').toISOString();
    appHome.nextRelease!.displayableVersion = nextReleaseVersion;
    appHome.nextRelease!.version = nextReleaseVersion;
    appHome.nextRelease!.id = nextReleaseId;
    appHome.nextRelease!.releaseId = nextReleaseId;

    if (appHome.upcomingReleases!.length > 0) {
        appHome.upcomingReleases![0].targetKickoffDate = dayjs().add(5, 'day').toISOString();
        appHome.upcomingReleases![0].targetReleaseDate = dayjs().add(10, 'day').toISOString();
        appHome.upcomingReleases![0].targetSubmitDate = dayjs().add(11, 'day').toISOString();
    }

    const appStoreType =
        getIsApplePlatform(platform) ? AppStoreType.Apple
        : getIsOTAPlatform(platform) ? undefined
        : AppStoreType.Google;

    appHome.nextRelease!.events = generateTimelineEventsHistory({
        currentReleaseId: appHome.nextRelease!.id,
        version: upcomingReleaseVersion,
        currentUser: undefined,
        appStoreType,
        platform,
        isReleaseComplete: false,
    });

    return appHome;
};

export const massageAppSettingsData: (platform: AppPlatform, appSettings: AppSettings) => AppSettings = (
    platform,
    appSettings,
) => {
    const isOTAPlatform = getIsOTAPlatform(platform);

    let integrationOverrides: Integration[] = [];

    if (isAndroidPlatform(platform)) {
        appSettings.automations = androidAutomations as Automation[];
        integrationOverrides = androidIntegrations as Integration[];
    }

    if (getIsOTAPlatform(platform)) {
        appSettings.automations = otaAutomations as Automation[];
        integrationOverrides = otaIntegrations as Integration[];
    }

    if (isIosPlatform(platform)) {
        appSettings.automations = iosAutomations as Automation[];
        integrationOverrides = iosIntegrations as Integration[];
    }

    appSettings.integrations = unionBy(
        [...appSettings.integrations, ...integrationOverrides],
        (integration) => integration.id,
    );

    const iosRoles: RecursiveStringifyEnums<RolesMap> = {
        approver: {
            appStore: {
                syncEnabled: false,
                roles: ['CUSTOMER_SUPPORT'],
            },
        },
        cx: {
            appStore: {
                syncEnabled: false,
                roles: ['CUSTOMER_SUPPORT'],
            },
        },
        design: {
            appStore: {
                syncEnabled: false,
                roles: ['MARKETING'],
            },
        },
        em: {
            appStore: {
                syncEnabled: false,
                roles: ['APP_MANAGER'],
            },
        },
        engineer: {
            appStore: {
                syncEnabled: false,
                roles: ['DEVELOPER'],
            },
        },
        marketing: {
            appStore: {
                syncEnabled: true,
                roles: ['MARKETING'],
            },
        },
        ops: {
            appStore: {
                syncEnabled: false,
                roles: ['CUSTOMER_SUPPORT'],
            },
        },
        pm: {
            appStore: {
                syncEnabled: false,
                roles: ['APP_MANAGER'],
            },
        },
        qa: {
            appStore: {
                syncEnabled: false,
                roles: ['CUSTOMER_SUPPORT'],
            },
        },
        pilot: {
            appStore: {
                syncEnabled: false,
                roles: ['CUSTOMER_SUPPORT'],
            },
        },
    };

    appSettings.rolesMap = iosRoles as RolesMap;

    if (isAndroidPlatform(platform)) {
        const androidRoles: RecursiveStringifyEnums<RolesMap> = {
            approver: {
                appStore: {
                    syncEnabled: false,
                    roles: ['CAN_ACCESS_APP'],
                },
            },
            cx: {
                appStore: {
                    syncEnabled: false,
                    roles: ['CAN_ACCESS_APP'],
                },
            },
            design: {
                appStore: {
                    syncEnabled: false,
                    roles: ['CAN_ACCESS_APP', 'CAN_MANAGE_PUBLIC_LISTING'],
                },
            },
            em: {
                appStore: {
                    syncEnabled: false,
                    roles: [
                        'CAN_ACCESS_APP',
                        'CAN_VIEW_FINANCIAL_DATA',
                        'CAN_MANAGE_ORDERS',
                        'CAN_MANAGE_TRACK_APKS',
                        'CAN_MANAGE_TRACK_USERS',
                        'CAN_MANAGE_PUBLIC_LISTING',
                        'CAN_REPLY_TO_REVIEWS',
                    ],
                },
            },
            engineer: {
                appStore: {
                    syncEnabled: false,
                    roles: ['CAN_MANAGE_TRACK_APKS', 'CAN_ACCESS_APP'],
                },
            },
            marketing: {
                appStore: {
                    syncEnabled: false,
                    roles: ['CAN_ACCESS_APP', 'CAN_MANAGE_PUBLIC_LISTING'],
                },
            },
            ops: {
                appStore: {
                    syncEnabled: false,
                    roles: ['CAN_ACCESS_APP'],
                },
            },
            pm: {
                appStore: {
                    syncEnabled: false,
                    roles: [
                        'CAN_ACCESS_APP',
                        'CAN_VIEW_FINANCIAL_DATA',
                        'CAN_MANAGE_ORDERS',
                        'CAN_MANAGE_TRACK_APKS',
                        'CAN_MANAGE_TRACK_USERS',
                        'CAN_MANAGE_PUBLIC_LISTING',
                        'CAN_REPLY_TO_REVIEWS',
                    ],
                },
            },
            qa: {
                appStore: {
                    syncEnabled: false,
                    roles: ['CAN_ACCESS_APP'],
                },
            },
            pilot: {
                appStore: {
                    syncEnabled: false,
                    roles: [
                        'CAN_ACCESS_APP',
                        'CAN_VIEW_FINANCIAL_DATA',
                        'CAN_MANAGE_ORDERS',
                        'CAN_MANAGE_TRACK_APKS',
                        'CAN_MANAGE_TRACK_USERS',
                        'CAN_MANAGE_PUBLIC_LISTING',
                        'CAN_REPLY_TO_REVIEWS',
                    ],
                },
            },
        };

        appSettings.rolesMap = androidRoles as RolesMap;

        appSettings.integrations = appSettings.integrations.map((integration) => {
            if (integration.id === IntegrationType.Apple) {
                return {
                    ...integration,
                    data: {
                        ...integration.data,
                        packageName: 'com.demo.demo',
                        availableTracks: ['internal', 'alpha', 'beta', 'production'],
                    },
                };
            } else if (integration.id === IntegrationType.GitHubVCS) {
                return {
                    ...integration,
                    data: {
                        ...integration.data,
                        repoName: 'demo-android',
                    },
                };
            } else if (integration.id === IntegrationType.Bitrise) {
                return {
                    ...integration,
                    data: {
                        ...integration.data,
                        appSlug: 'demo-android',
                        appTitle: 'demo-android',
                    },
                };
            } else if (integration.id === IntegrationType.TestRail) {
                return {
                    ...integration,
                    data: {
                        ...integration.data,
                        selectedProject: {
                            id: '2',
                            name: 'Demo Testrail Android',
                            isSingleSuite: true,
                        },
                        testRunNameKeywords: ['Android'],
                        testRunNameKeywordsMatchAny: false,
                    },
                };
            }

            return integration;
        });

        appSettings.monitoringSettings.metrics = appSettings.monitoringSettings.metrics.map((metric) => {
            if (metric.integrationId === IntegrationType.Apple) {
                return {
                    ...metric,
                    integrationId: IntegrationType.Google,
                };
            }

            return metric;
        });

        appSettings.bumpVersionFiles = [
            {
                fileName: 'app/build.gradle',
                currentVersion: '1.1.0',
                branch: 'development',
            },
        ];

        appSettings.appStoreType = AppStoreType.Google;
    } else if (isOTAPlatform) {
        appSettings.bumpVersionFiles = [
            {
                fileName: 'package.json',
                currentVersion: '1.0.1',
                branch: 'development',
            },
        ];

        appSettings.releaseBranchPatterns = appSettings.releaseBranchPatterns.map((pattern) => {
            return {
                ...pattern,
                pattern: 'codepush-{version}',
            };
        });

        appSettings.monitoringSettings.metrics = appSettings.monitoringSettings.metrics.filter(
            (metric) => metric.category !== AppMonitoringHealthMetricCategory.UserReviews,
        );
    }

    const disabledAutomationsForPlatform = isOTAPlatform ? [AutomationType.AutoDownloadArtifacts] : [];

    appSettings.automations.map((automation) => {
        if (disabledAutomationsForPlatform.includes(automation.type)) {
            return {
                ...automation,
                enabled: false,
            };
        }

        return automation;
    });

    return appSettings;
};

export const createAppsDemoModeData: () => DemoAppsPayload = () => {
    // 1. pull fake app, reproduce it for each platform, change appIds, change app names, tweak app settings, tweak app home
    // 2. pull fake app group, populate app ids
    // 3. pull fake releases for each platform

    const platforms = [AppPlatform.Ios, AppPlatform.Android, AppPlatform.ReactNativeOTA];
    const apps: AppDemoData[] = [];
    const releases: DemoReleasePayload[] = [];
    const releasesByApp: Record<string, DemoReleasePayload[]> = {};

    const integrationProviders = [...mockedIntegrationProviders.integrationProviders] as IntegrationProvider[];

    forEach(platforms, (platform) => {
        const appReleases: DemoReleasePayload[] = [];
        const app = legacyAppMock(platform) as AppEntity;
        const appHome = appHomeMock(platform) as unknown as AppHome;

        const liveReleaseVersionForPlatform = getIsOTAPlatform(platform) ? '1.0.1' : '1.0.0';
        const liveReleaseId = `${app.appId}:${liveReleaseVersionForPlatform}`;

        const liveRelease = mockReleaseForPlatform(platform) as Release;
        let releaseSteps = baseReleaseStepsForPlatform(platform) as ReleaseStep[];

        const massagedAppSettings = massageAppSettingsData(platform, {
            ...app.appSettings,
        });

        const injectAutomationSteps = (releaseSteps: ReleaseStep[]) => {
            releaseSteps.map((step) => {
                step.automationRuns = getAutomationStepsByPlatform(platform, step.type, false);
                step.automationRuns = step.automationRuns.map((automation) => {
                    const appSettingsAutomation = massagedAppSettings.automations.find((appSettingsAutomation) => {
                        return appSettingsAutomation.type === automation.type;
                    });

                    return {
                        ...automation,
                        ...appSettingsAutomation,
                        enabled: true,
                    };
                });

                return step;
            });
        };

        liveRelease.version = liveReleaseVersionForPlatform;

        liveRelease.displayableVersion = liveReleaseVersionForPlatform;
        liveRelease.status = ReleaseStatus.Completed;
        liveRelease.timelinePhase = ReleaseTimelinePhase.Completed;
        liveRelease.id = liveReleaseId;
        liveRelease.releaseId = liveReleaseId;
        liveRelease.submittedAt = dayjs().subtract(2, 'day').toISOString();
        liveRelease.releasedAt = dayjs().subtract(1, 'day').toISOString();
        const phasedRelease = mockPhasedReleaseForPlatform(platform, liveRelease.releasedAt)!;
        liveRelease.phasedRelease = phasedRelease;
        if (isIosPlatform(platform)) {
            let reviewSubmissionIOSAppStoreData = mockReviewSubmissionAppStoreData({
                phasedRelease,
            });
            reviewSubmissionIOSAppStoreData = {
                ...reviewSubmissionIOSAppStoreData,
                relevantReviewSubmission: {
                    ...reviewSubmissionIOSAppStoreData.relevantReviewSubmission!,
                    state: AppStoreReviewSubmissionState.Complete,
                    reviewSubmissionItems:
                        reviewSubmissionIOSAppStoreData.relevantReviewSubmission!.reviewSubmissionItems.map((item) => {
                            return {
                                ...item,
                                state: AppStoreReviewSubmissionItemState.Approved,
                            };
                        }),
                } as typeof reviewSubmissionIOSAppStoreData.relevantReviewSubmission,
                versionReviewSubmissionItem: {
                    ...reviewSubmissionIOSAppStoreData.versionReviewSubmissionItem,
                    state: AppleAppStoreState.ReadyForSale,
                } as typeof reviewSubmissionIOSAppStoreData.versionReviewSubmissionItem,
            };
            releaseSteps = injectAppStoreData(releaseSteps, reviewSubmissionIOSAppStoreData);
        }
        liveRelease.isReleaseTagged = true;
        liveRelease.isSubmitted = true;
        liveRelease.isReleased = true;
        injectAutomationSteps(releaseSteps);
        releaseSteps = fillStepsWithReleaseData(releaseSteps as ReleaseStep[], liveRelease, platform);

        releaseSteps = releaseSteps.map((step) => {
            if (step.type === ReleaseStepType.Takeoff) {
                const data = step.data as ReleaseStepTakeoffData;

                return {
                    ...step,
                    status: ReleaseStepStatus.Ready,
                    statusReasonString: 'Released!',
                    data: {
                        ...data,
                        appStoreData: {
                            ...data.appStoreData,
                            state:
                                isIosPlatform(platform) ?
                                    AppleAppStoreState.ReadyForSale
                                :   GooglePlayStoreState.Completed,
                        },
                        tagData: {
                            releaseTagState: ReleaseTagState.Tagged,
                            releaseTag: {
                                name: `v${liveReleaseVersionForPlatform}`,
                                url: 'https://github.com',
                                isAuthorRunway: false,
                                commitHash: '1bc234v',
                                commitMessage: 'Bumping version',
                                commitURL: 'https://github.com',
                            },
                        },
                    },
                };
            } else if (step.type === ReleaseStepType.ReleaseCandidate) {
                const data = step.data as ReleaseStepReleaseCandidateData;

                return {
                    ...step,
                    status: ReleaseStepStatus.Ready,
                    data: {
                        ...data,
                        builds: data.builds!.map((build) => {
                            build.ciBuild.artifacts = build.ciBuild.artifacts!.map((artifact) => {
                                artifact.version = liveRelease.version;
                                if (artifact.binaryBuildDetails) {
                                    artifact.binaryBuildDetails.versionString = liveRelease.version;
                                }
                                return artifact;
                            });
                            return build;
                        }),
                    },
                };
            }
            return {
                ...step,
                status: ReleaseStepStatus.Ready,
                statusReasonString: 'Step completed!',
            };
        });

        liveRelease.stepRunGraph = releaseStepsToStepRunGraph(releaseSteps);

        const featureFlags = generateFeatureFlags(platform);
        const massagedLiveRelease = massageTestReleaseData(
            platform,
            liveRelease,
            releaseSteps,
            app.appSettings.usesStaticReleaseBranchForNonHotfixes,
            featureFlags as FeatureFlag[],
            app.appSettings.defaultReleaseType,
            undefined,
            undefined,
            'ssullenberger@demo.com',
        );

        const upcomingRelease = upcomingReleaseMock(platform) as Release;
        let upcomingReleaseSteps = baseReleaseStepsForPlatform(platform) as ReleaseStep[];

        const upcomingReleaseVersionForPlatform = getIsOTAPlatform(platform) ? '1.0.3' : '1.2.0';
        const upcomingReleaseId = `${app.appId}:${upcomingReleaseVersionForPlatform}`;

        upcomingRelease.version = upcomingReleaseVersionForPlatform;
        upcomingRelease.displayableVersion = upcomingReleaseVersionForPlatform;
        upcomingRelease.id = upcomingReleaseId;
        upcomingRelease.releaseId = upcomingReleaseId;
        upcomingRelease.releaseOverview = generateReleaseOverview({
            targetKickoffDate: upcomingRelease.targetKickoffDate,
            targetReleaseDate: upcomingRelease.targetReleaseDate,
            targetSubmitDate: upcomingRelease.targetSubmitDate,
            kickOffDate: upcomingRelease.targetKickoffDate,
        });
        upcomingRelease.releaseStatsHistory = generateReleaseStatsHistory({
            version: upcomingReleaseVersionForPlatform,
            platform,
            isCompletedRelease: false,
        });

        upcomingReleaseSteps = fillStepsWithReleaseData(
            upcomingReleaseSteps as ReleaseStep[],
            upcomingRelease,
            platform,
        );

        injectAutomationSteps(upcomingReleaseSteps);

        let rollbackRelease: Release | undefined;
        let rollbackReleaseSteps: ReleaseStep[] | undefined;
        let rollbackReleaseId: string | undefined;
        const supportsRollbacks = [AppPlatform.Ios, AppPlatform.Android].includes(platform);
        if (supportsRollbacks) {
            rollbackRelease = rollbackReleaseMock(platform, appHome.liveRelease!) as Release;
            rollbackReleaseSteps = generateRollbackSteps(appHome.liveRelease!) as ReleaseStep[];
            if (isIosPlatform(platform)) {
                const reviewSubmissionIOSAppStoreData = mockReviewSubmissionAppStoreData({
                    phasedRelease: undefined,
                });
                rollbackReleaseSteps = injectAppStoreData(rollbackReleaseSteps, {
                    ...reviewSubmissionIOSAppStoreData,
                    relevantReviewSubmission: {
                        ...reviewSubmissionIOSAppStoreData.relevantReviewSubmission,
                        state: AppStoreReviewSubmissionState.Complete,
                        reviewSubmissionItems: [],
                    } as typeof reviewSubmissionIOSAppStoreData.relevantReviewSubmission,
                });
            }
            rollbackReleaseId = `${app.appId}:${rollbackRelease.displayableVersion}`;
            rollbackRelease.releaseId = rollbackReleaseId;
            rollbackRelease.id = rollbackReleaseId;
            rollbackRelease.releaseOverview = generateReleaseOverview({
                targetKickoffDate: rollbackRelease.targetKickoffDate,
                targetReleaseDate: rollbackRelease.targetReleaseDate,
                targetSubmitDate: rollbackRelease.targetSubmitDate,
                kickOffDate: rollbackRelease.targetKickoffDate,
            });
            rollbackRelease.releaseStatsHistory = generateReleaseStatsHistory({
                version: rollbackRelease.displayableVersion,
                platform,
                isCompletedRelease: false,
            });

            const rollbackStep = rollbackReleaseSteps.find(
                (step) => step.type === ReleaseStepType.RollbackBuild,
            ) as ReleaseStepRollbackBuild;
            const associatedAppStore = platform === AppPlatform.Ios ? IntegrationType.Apple : IntegrationType.Google;
            if (rollbackStep) {
                const artifact = rollbackStep.data.build!.artifacts[0];
                artifact.fileName = `${artifact.fileName}${platform === AppPlatform.Ios ? '.ipa' : '.aab'}`;
                artifact.binaryBuildDetails!.artifactType =
                    platform === AppPlatform.Ios ? BuildArtifactType.IOSAppStore : BuildArtifactType.AndroidAAB;
                rollbackStep.data.build!.associatedAppStoreBuild!.integrationId = associatedAppStore;
                if (platform === AppPlatform.Ios) {
                    artifact.binaryBuildDetails!.bundleName = 'com.apollo.apollo-ios';
                }
            }
            rollbackStep.data.sourceVersion!.completedReleaseSummary.appStoreBuilds.forEach(
                (build: AppStoreAvailableBuild) => {
                    build.appStoreBuild.integrationId = associatedAppStore;
                },
            );

            const rolloutSubmissionReviewAndReleaseSteps = rollbackReleaseSteps?.filter(
                (step) =>
                    step.type === ReleaseStepType.Submission ||
                    step.type === ReleaseStepType.StoreReview ||
                    step.type === ReleaseStepType.Takeoff,
            );

            rolloutSubmissionReviewAndReleaseSteps?.forEach((step) => {
                const stepData = step.data as
                    | ReleaseStepSubmissionData
                    | ReleaseStepStoreReviewData
                    | ReleaseStepTakeoffData;
                stepData.appStoreSelectedBuilds?.forEach((build) => {
                    build.appStoreBuild.integrationId = associatedAppStore;
                });
                stepData.appStoreAvailableBuilds?.forEach((build) => {
                    build.appStoreBuild.integrationId = associatedAppStore;
                });
            });

            if (platform === AppPlatform.Ios) {
                const submissionStep = rollbackReleaseSteps.find((step) => step.type === ReleaseStepType.Submission)!;
                submissionStep.status = ReleaseStepStatus.Ready;
                submissionStep.statusReasonString = 'Your app has been submitted!';

                const reviewStep = rollbackReleaseSteps.find((step) => step.type === ReleaseStepType.StoreReview)!;
                reviewStep.status = ReleaseStepStatus.Ready;
                reviewStep.statusReasonString = 'Your app has been approved!';

                const releaseStep = rollbackReleaseSteps.find((step) => step.type === ReleaseStepType.Takeoff)!;
                releaseStep.status = ReleaseStepStatus.Pending;
                releaseStep.statusReasonString = 'Waiting for manual release.';
            }
        }

        const currentReleaseVersionForPlatform = getIsOTAPlatform(platform) ? '1.0.2' : '1.1.0';
        const currentReleaseId = `${app.appId}:${currentReleaseVersionForPlatform}`;

        // next release
        const release = mockReleaseForPlatform(platform) as Release;

        release.version = currentReleaseVersionForPlatform;
        release.displayableVersion = currentReleaseVersionForPlatform;
        injectAutomationSteps(releaseSteps);
        release.id = currentReleaseId;
        release.releaseId = currentReleaseId;

        // const releaseStepsData =
        const releaseSubmissionReviewAndReleaseSteps = releaseSteps?.filter(
            (step) =>
                step.type === ReleaseStepType.Submission ||
                step.type === ReleaseStepType.StoreReview ||
                step.type === ReleaseStepType.Takeoff,
        );

        releaseSubmissionReviewAndReleaseSteps?.forEach((step) => {
            const stepData = step.data as
                | ReleaseStepSubmissionData
                | ReleaseStepStoreReviewData
                | ReleaseStepTakeoffData;
            stepData.appStoreSelectedBuilds = stepData.appStoreSelectedBuilds?.map((build) => {
                return {
                    ...build,
                    stabilityInfo: {
                        ...build.stabilityInfo,
                        sessionAdoption: 0.0,
                    },
                };
            }) as typeof stepData.appStoreSelectedBuilds;
        });

        let { testRelease, testReleaseSteps } = massageTestReleaseData(
            platform,
            release,
            releaseSteps,
            app.appSettings.usesStaticReleaseBranchForNonHotfixes,
            featureFlags as FeatureFlag[],
            app.appSettings.defaultReleaseType,
            undefined,
            undefined,
            'ssullenberger@demo.com',
        );

        testReleaseSteps = fillStepsWithReleaseData(testReleaseSteps as ReleaseStep[], testRelease, platform);

        const rolloutMonitoring = massageRolloutMonitoring(platform, baseRolloutData(platform) as RolloutMonitoring);

        const upcomingReleasePlanningSummary = (
            testReleaseSteps.find((step) => step.type === ReleaseStepType.Kickoff) as ReleaseStepKickoff
        )?.data.releasePlanningSummary;

        const upcomingReleases: UpcomingReleaseSummary[] = [
            {
                ...upcomingRelease,
                planningSummary: (upcomingReleasePlanningSummary ?? undefined) as string | undefined,
                releasePilotId: upcomingRelease.releasePilotId ?? undefined,
                events: upcomingRelease.timelineEventsHistory ?? [],
                id: upcomingRelease.id,
                releaseId: upcomingReleaseId,
                featuresStep: getCompactDataFromFeaturesStep(featuresStep(platform)),
                steps: upcomingReleaseSteps,
                releaseName: undefined,
            },
        ];
        const massagedAppHome = massageAppHomeData(
            platform,
            {
                ...appHome,
                liveRelease: {
                    ...appHome.liveRelease,
                    id: liveRelease.id,
                    releaseId: liveRelease.releaseId,
                    releaseDate: liveRelease.releasedAt,
                    phasedRelease: liveRelease.phasedRelease,
                } as AppHome['liveRelease'],
                upcomingReleases: upcomingReleases,
                nextRelease: {
                    ...appHome.nextRelease,
                    id: release.id,
                    releaseId: release.releaseId,
                } as AppHome['nextRelease'],
            },
            liveReleaseId,
            liveReleaseVersionForPlatform,
            currentReleaseId,
            currentReleaseVersionForPlatform,
            upcomingReleaseId,
            upcomingReleaseVersionForPlatform,
            rolloutMonitoring,
        );

        app.appSettings = massagedAppSettings;
        app.appHome = massagedAppHome;
        app.appSettings.platform = platform;

        const releaseIds = [liveReleaseId, currentReleaseId, upcomingReleaseId];
        if (rollbackRelease) {
            releaseIds.push(rollbackReleaseId!);
        }

        apps.push({
            app,
            releaseIds: releaseIds,
        });

        appReleases.push({
            release: { ...testRelease },
            releaseId: testRelease.releaseId,
            releaseSteps: releaseSteps,
        });
        appReleases.push({
            release: {
                ...massagedLiveRelease.testRelease,
                rolloutMonitoring,
            },
            releaseId: liveRelease.releaseId,
            releaseSteps: testReleaseSteps,
        });
        appReleases.push({
            release: {
                ...upcomingRelease,
            },
            releaseId: upcomingRelease.releaseId,
            releaseSteps: upcomingReleaseSteps,
        });
        if (rollbackRelease) {
            appReleases.push({
                release: {
                    ...rollbackRelease,
                },
                releaseId: rollbackRelease.releaseId,
                releaseSteps: rollbackReleaseSteps!,
            });
        }

        releasesByApp[app.id] = appReleases;
        releases.push(...appReleases);
    });

    const statistics = generateOrgOverviewStats();
    const buildDistroDemoData = generateBuildDistributionDemoData();

    // Set up workflows apps
    const crossPlatformAppId = 'gemini';
    const crossPlatformAppSettings = mockCrossPlatformAppSettings(crossPlatformAppId);
    const crossPlatformReleases = mockCrossPlatformAppReleases(
        crossPlatformAppId,
        crossPlatformAppSettings.destinations,
        crossPlatformAppSettings.stepGraph,
    );
    const crossPlatform = createWorkflowApp(crossPlatformAppId, crossPlatformAppSettings, crossPlatformReleases);
    apps.push({
        app: crossPlatform.app,
        releaseIds: crossPlatform.releases.map((release) => release.releaseId),
    });
    releases.push(...crossPlatform.releases);
    releasesByApp[crossPlatform.app.id] = crossPlatform.releases;

    const whiteLabelAppId = 'mercury';
    const whiteLabelAppSettings = mockWhiteLabelAppSettings(whiteLabelAppId);
    const whiteLabelReleases = mockWhiteLabelAppReleases(
        whiteLabelAppId,
        whiteLabelAppSettings.destinations,
        whiteLabelAppSettings.stepGraph,
    );
    const whiteLabel = createWorkflowApp(whiteLabelAppId, whiteLabelAppSettings, whiteLabelReleases);
    apps.push({
        app: whiteLabel.app,
        releaseIds: whiteLabel.releases.map((release) => release.releaseId),
    });
    releases.push(...whiteLabel.releases);
    releasesByApp[whiteLabel.app.id] = whiteLabel.releases;

    // Merge step runs and org destinations from both workflow apps
    const mergedStepRunsDemoData = {
        ...crossPlatform.stepRunsDemoData,
        ...whiteLabel.stepRunsDemoData,
    };

    const mergedStepConfigsDemoData = {
        ...crossPlatform.stepConfigsDemoData,
        ...whiteLabel.stepConfigsDemoData,
    };

    const appDestinations: AppDestinationsAndVersions[] = [];
    const allDestinationIds: string[] = [];
    const allVersions: string[] = [];
    const summaryPreferences: Record<string, OrgSummaryPreferences> = {};

    for (const demoApp of apps) {
        const { app } = demoApp;
        const appVersions = releasesByApp[app.id].map((r) => r.release.version);
        const destinationIds = app.appSettings.destinations.map((d) => d.stepConfigId);

        allVersions.push(...appVersions);
        allDestinationIds.push(...destinationIds);
        appDestinations.push({
            appId: app.appId,
            appName: app.appSettings.appName,
            destinations: app.appSettings.destinations.map((dest) => ({
                stepConfigId: dest.stepConfigId,
                name: dest.name,
                platform: dest.platform,
                appStoreType: dest.appStoreType,
                iconURL: dest.iconURL,
            })),
            versions: appVersions,
            renderWorkflowsOverview: [crossPlatform.app.appId, whiteLabel.app.appId].includes(app.appId),
        });

        const initialPageView = app.id === whiteLabel.app.appId ? PageViewType.Tabular : PageViewType.Default;
        summaryPreferences[app.id] = {
            pageView: initialPageView,
            tabularLayout: TabularLayoutType.Standard,
            // Reducing total number of rows to avoid lagging
            selectedDestinationIds: app.appSettings.destinations.map((dest) => dest.stepConfigId).slice(0, 6),
            selectedVersions: appVersions.slice(0, 6),
            selectedMetrics: [],
        };
    }

    const orgId = 'demo';
    const orgDestinations: Record<string, OrgDestinationsResponse> = {};
    orgDestinations[orgId] = {
        orgId,
        canAccessTabularView: true,
        appDestinations,
        allDestinationIds,
        allVersions,
    };

    const summaryViewByRequestId: Record<string, SummaryViewResponseAPI> = {};
    const summaryViewItems: VersionSummaryViewDataItemAPI[] = [];
    apps.forEach(({ app }) => {
        const requestId = createSummaryRequestId('demo', app.appId);
        const summaryView =
            app.workflowOverview ?
                convertWorkflowOverviewToSummaryView(app.workflowOverview, mergedStepRunsDemoData)
            :   convertAppHomeToSummaryView(app.appHome!, app.appSettings);

        summaryViewByRequestId[requestId] = summaryView;
        summaryViewItems.push(...summaryView.versionSummaryViewItems);
    });

    // Org-level summary view
    const orgLevelRequestId = createSummaryRequestId('demo');
    summaryViewByRequestId[orgLevelRequestId] = {
        versionSummaryViewItems: summaryViewItems,
        availableTotalCount: summaryViewItems.length,
        selectedTotalCount: summaryViewItems.length,
    };

    const orgUsersDemoData = mockOrgUsers.reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
    }, {} as StringKeyedDictionary<OrgUser>);

    return {
        apps,
        appGroups: appGroupsMock,
        releases,
        integrationProviders,
        statistics,
        buildDistroDemoData,
        stepRunsDemoData: mergedStepRunsDemoData,
        stepConfigsDemoData: mergedStepConfigsDemoData,
        orgDestinations,
        summaryPreferences,
        summaryViewByRequestId,
        orgUsersDemoData,
    };
};

export const releaseStepsToStepRunGraph = (steps: RecursiveStringifyEnums<ReleaseStep>[]): WorkflowRunStepRunGraph => {
    const stepsByGroupIndex = groupBy(steps, 'groupIndex');

    const appStoreData = (steps.find((step) => step.type === ReleaseStepType.Takeoff)?.data as ReleaseStepTakeoffData)
        ?.appStoreData;
    const isPhasedRelease = appStoreData ? 'phasedRelease' in appStoreData : false;

    const stepGroups = Object.keys(stepsByGroupIndex).map((groupIndex) => {
        const groupSteps = stepsByGroupIndex[groupIndex];

        groupSteps.sort((a, b) => a.orderIndex! - b.orderIndex!);

        const archetypeGroups = groupSteps.map((step) => {
            return {
                id: step.id,
                name: displayTextForReleaseStepType(step.type!, [], [], false), // passing empty arrays for platform and appStoreType, which will result in general / default text.
                archetype: step.type,
                combinedStatus: step.status,
                stepRunStatusDatum: [getStepStatusData(step, isPhasedRelease)],
            };
        });

        return {
            stepArchetypeGroups: archetypeGroups,
        };
    }) as WorkflowRunStepRunGraph['stepGroups'];

    return {
        legacyReleaseId: '',
        stepGroups,
    };
};

export const getStepStatusData = (
    step: RecursiveStringifyEnums<ReleaseStep>,
    isPhasedRelease: boolean,
): StepRunStatusData => {
    let hasEnabledLifecycleAutomation = false;
    let hasEnabledPendingLifecycleAutomation = false;
    let hasEnabledFailedLifecycleAutomation = false;
    let hasEnabledBlockedLifecycleAutomation = false;

    for (const automation of step.automationRuns!) {
        if (!automation.isLifecycle) {
            continue;
        }

        if (!automation.status) {
            continue;
        }

        if (
            automation.status !== AutomationStatus.Inactive &&
            automation.status !== AutomationStatus.Skipped &&
            automation.status !== AutomationStatus.Paused
        ) {
            hasEnabledLifecycleAutomation = true;

            if (
                automation.status === AutomationStatus.Pending ||
                automation.status === AutomationStatus.Active ||
                automation.status === AutomationStatus.Incomplete
            ) {
                hasEnabledPendingLifecycleAutomation = true;
            } else if (automation.status === AutomationStatus.Failed) {
                hasEnabledFailedLifecycleAutomation = true;
            } else if (automation.status === AutomationStatus.Blocked) {
                hasEnabledBlockedLifecycleAutomation = true;
            }
        }
    }
    const pendingChecklistItemCount = step.checklistItems!.filter((item) => !!item.status).length;

    let extraData:
        | ReleaseStepRunStatusData['extraData']
        | VCSDistributionStepRunStatusData['extraData']
        | CIDistributionStepRunStatusData['extraData']
        | undefined = undefined;

    switch (step.type) {
        case ReleaseStepType.Takeoff: {
            const data = step.data as ReleaseStepTakeoffData;
            if (data.tagData) {
                extraData = {
                    releaseTagState: data.tagData.releaseTagState,
                    isPhasedReleaseInProgress: isPhasedRelease,
                    isPhasedReleaseSuperseded: false,
                } as ReleaseStepRunStatusData['extraData'];
            }
            break;
        }
        case ReleaseStepType.VCSDistribution: {
            const vcsData = step.data as ReleaseStepVCSDistributionData;
            if (vcsData.tagData) {
                extraData = {
                    releaseTagState: vcsData.tagData.releaseTagState,
                } as VCSDistributionStepRunStatusData['extraData'];
            }
            break;
        }
        case ReleaseStepType.CIDistribution: {
            const ciData = step.data as ReleaseStepCIDistributionData;
            if (ciData.tagData) {
                extraData = {
                    releaseTagState: ciData.tagData.releaseTagState,
                } as CIDistributionStepRunStatusData['extraData'];
            }
            break;
        }
    }

    return {
        stepRunId: '',
        stepConfigId: '',
        status: step.status,
        statusReasonString: step.statusReasonString,
        name: null,

        type: step.type,

        hasEnabledLifecycleAutomation,
        hasEnabledPendingLifecycleAutomation,
        hasEnabledFailedLifecycleAutomation,
        hasEnabledBlockedLifecycleAutomation,

        pendingChecklistItemCount,

        extraData,
    } as unknown as StepRunStatusData;
};

const massageRolloutMonitoring: (platform: AppPlatform, rolloutMonitoring: RolloutMonitoring) => RolloutMonitoring = (
    platform,
    rolloutMonitoring,
) => {
    const disallowedMetricsForPlatform: AppMonitoringHealthMetricType[] =
        getIsOTAPlatform(platform) ? [UserRatingsMetricType.AvgUserRating] : [];

    if (platform === AppPlatform.Android) {
        rolloutMonitoring.monitoringAutomations = rolloutMonitoring.monitoringAutomations.map((automation) => {
            if (automation.id === AutomationType.AutoPrepareRollbackRelease) {
                return {
                    ...automation,
                    steps: [
                        automation.steps[0],
                        {
                            status: AutomationStatus.Pending,
                            description: 'Manual rollback submit for review',
                        },
                    ],
                };
            }

            return automation;
        });
    } else if (platform === AppPlatform.ReactNativeOTA) {
        // No rollout automations for OTA platforms
        rolloutMonitoring.monitoringAutomations = [];
        rolloutMonitoring.automationRuns = [];
    }

    return {
        ...rolloutMonitoring,
        monitoringStatistics: rolloutMonitoring.monitoringStatistics.filter(
            (stat) => !disallowedMetricsForPlatform.includes(stat.type),
        ),
    };
};

export const getReleaseCalendarDemoData = (app: AppEntity, release?: Release) => {
    let mockData: Record<string, ReleaseCalendarData> = {};
    if (app.workflowId && release?.releaseId) {
        mockData = generateReleaseCalendarForWorkflowRun(release);
    } else {
        mockData = generateReleaseCalendarDemoData(app.appSettings.platform, release?.releaseId);
    }
    return getLifecycleAndReleaseCycleEvents(mockData);
};

// Helper to find stepConfigId by archetype and platform
const findStepConfigIdByArchetypeAndPlatformInGraph = (
    stepGraph: WorkflowStepGraph,
    archetype: ReleaseStepType,
    platform: AppPlatform,
): string | null => {
    for (const stepGroup of stepGraph.stepGroups) {
        for (const archetypeGroup of stepGroup.archetypeGroups) {
            if (archetypeGroup.archetype === archetype) {
                const matchingStep = archetypeGroup.steps.find((step) => {
                    const stepId = step.stepConfigId.toLowerCase();
                    const platformStr = platform.toLowerCase();
                    return stepId.includes(platformStr);
                });
                if (matchingStep) {
                    return matchingStep.stepConfigId;
                }
            }
        }
    }
    return null;
};

export const createWorkflowApp = (
    appName: string,
    appSettings: AppSettings,
    releases: Release[],
): {
    app: AppEntity;
    releases: DemoReleasePayload[];
    stepRunsDemoData: StringKeyedDictionary<StepRun>;
    stepConfigsDemoData: StringKeyedDictionary<StepConfig>;
} => {
    const app = mockWorkflowsApp(appName, appSettings);
    const workflowOverview = mockWorkflowOverview(app.id, app.workflowId!, appSettings, releases);
    app.workflowOverview = workflowOverview;

    const demoReleases: DemoReleasePayload[] = releases.map((r) => {
        const releaseWithSteps = {
            ...r,
            steps: createNonMigratedReleaseStepsForWorkflow(),
        };
        return {
            releaseId: r.releaseId,
            release: releaseWithSteps,
            releaseSteps: releaseWithSteps.steps,
        };
    });

    const stepRunsDemoData = extractStepRunsFromReleases(
        demoReleases.map((dr) => dr.release),
        appSettings.stepGraph,
        appSettings.destinations,
    );

    const stepConfigsDemoData: StringKeyedDictionary<StepConfig> = {};
    appSettings.stepGraph.stepGroups.forEach((stepGroup) => {
        stepGroup.archetypeGroups.forEach((archetypeGroup) => {
            archetypeGroup.steps.forEach((step) => {
                const stepDestinations = appSettings.destinations.filter((dest) =>
                    step.destinationStepConfigIds.includes(dest.stepConfigId),
                );

                const stepConfig = mockStepConfigForArchetype(
                    step.stepConfigId,
                    step.stepName,
                    step.archetype,
                    stepDestinations,
                    step.integrationInstallationIds,
                );

                stepConfigsDemoData[step.stepConfigId] = stepConfig;
            });
        });
    });

    const allStepAutomations = {
        ...iosStepAutomations,
        ...androidStepAutomations,
    };
    for (const automation of appSettings.automations) {
        const automationArchetype = Object.keys(allStepAutomations).find((archetype) =>
            allStepAutomations[archetype].some((a) => a.type === automation.type),
        ) as ReleaseStepType | undefined;

        if (automationArchetype) {
            const stepConfigId = findStepConfigIdByArchetypeAndPlatformInGraph(
                app.appSettings.stepGraph,
                automationArchetype,
                automation.platform,
            );
            if (stepConfigId) {
                automation.stepConfigId = stepConfigId;
            }
        }
    }

    return {
        app,
        releases: demoReleases,
        stepRunsDemoData,
        stepConfigsDemoData,
    };
};

const extractStepRunsFromReleases = (
    releases: Release[],
    stepGraph: WorkflowStepGraph,
    destinations: Destination[],
): StringKeyedDictionary<StepRun> => {
    const destinationsByStepConfigId = destinations.reduce(
        (acc, dest) => ({ ...acc, [dest.stepConfigId]: dest }),
        {} as Record<string, Destination>,
    );

    const stepToDestinationStepConfigIds = buildStepConfigToDestinationsMap(stepGraph);

    const stepRunsMap: StringKeyedDictionary<StepRun> = {};
    releases.forEach((release) => {
        release.stepRunGraph.stepGroups.forEach((stepGroup) => {
            stepGroup.stepArchetypeGroups.forEach((archetypeGroup) => {
                archetypeGroup.stepRunStatusDatum.forEach((stepRunData) => {
                    const destinationStepConfigIds = stepToDestinationStepConfigIds[stepRunData.stepConfigId!];
                    const stepDestinations: Destination[] =
                        destinationStepConfigIds?.map((id) => destinationsByStepConfigId[id])?.filter(Boolean) ?? [];

                    if (!stepDestinations?.length) {
                        return;
                    }

                    const platform = stepDestinations.length === 1 ? stepDestinations[0].platform : AppPlatform.Ios;

                    let stepData: any;
                    let automationRuns: AutomationRun[];
                    let checklistItems: ChecklistItem[];
                    let blockingConfig: ReleaseStepBlockingConfig;

                    // Rollout is handled separately since it's not in baseReleaseStepsForPlatform
                    if (archetypeGroup.archetype === ReleaseStepType.Rollout) {
                        const {
                            automationRuns: autoRuns,
                            checklistItems: clis,
                            ...rolloutData
                        } = baseRolloutData(platform, release.releasedAt, release.version);

                        stepData = rolloutData;
                        automationRuns = autoRuns as AutomationRun[];
                        checklistItems = clis as ChecklistItem[];
                        blockingConfig = ReleaseStepBlockingConfig.NonBlocking;
                    } else {
                        const baseReleaseStep = baseReleaseStepsForPlatform(platform)!.find(
                            (step) => step!.type === archetypeGroup.archetype,
                        );

                        if (!baseReleaseStep) {
                            return;
                        }

                        stepData = baseReleaseStep.data;
                        automationRuns = getAutomationStepsByPlatform(platform, archetypeGroup.archetype, true);
                        checklistItems = baseReleaseStep.checklistItems as ChecklistItem[];
                        blockingConfig = baseReleaseStep.blockingConfig as ReleaseStepBlockingConfig;
                    }

                    if (archetypeGroup.archetype === ReleaseStepType.Takeoff) {
                        const takeoffData = stepData as ReleaseStepTakeoffData;
                        const phasedReleaseFraction =
                            stepRunData.status === ReleaseStepStatus.Ready ?
                                Math.round((Math.random() * 0.9 + 0.1) * 100) / 100
                            :   0;

                        if ('phasedRelease' in takeoffData.appStoreData!) {
                            stepData.appStoreData.phasedRelease = {
                                ...takeoffData.appStoreData.phasedRelease,
                                phasedReleasePercent: phasedReleaseFraction * 100,
                                userFraction: phasedReleaseFraction,
                            };
                        }
                    }

                    stepRunsMap[stepRunData.stepRunId!] = {
                        id: stepRunData.stepRunId!,
                        stepId: stepRunData.stepConfigId!,
                        type: archetypeGroup.archetype,
                        displayName: stepRunData.name,
                        status: stepRunData.status,
                        statusReasonString: getMockedStatusReasonByArchetypeAndStatus(
                            archetypeGroup.archetype,
                            stepRunData.status,
                        ),
                        data: { summaryData: cloneDeep(stepData), complementaryData: {} },
                        automationRuns: cloneDeep(automationRuns) as AutomationRun[],
                        checklistItems: cloneDeep(checklistItems) as ChecklistItem[],
                        blockingConfig: blockingConfig,
                        destinations: stepDestinations,
                        stepTransactions: [],
                        stepConfigSnapshot: {
                            settings: {},
                            integrationInstallationIds: [],
                        },
                    };
                });
            });
        });
    });
    return stepRunsMap;
};

const createNonMigratedReleaseStepsForWorkflow = (): ReleaseStep[] => {
    const stepTypes: ReleaseStepType[] = [ReleaseStepType.Kickoff, ReleaseStepType.FeatureReadiness];
    return baseReleaseStepsForPlatform(AppPlatform.Ios)!.filter((step) =>
        stepTypes.includes(step!.type as ReleaseStepType),
    ) as ReleaseStep[];
};

const getAutomationStepsByPlatform = (
    platform: AppPlatform,
    archetype: ReleaseStepType,
    isWorkflows: boolean,
): AutomationRun[] => {
    const stepAutomations = isWorkflows ? workflowOnlyStepAutomations[archetype] : [];

    if (isAndroidPlatform(platform)) {
        return [...androidStepAutomations[archetype], ...stepAutomations] as AutomationRun[];
    }

    if (getIsOTAPlatform(platform)) {
        return [...otaStepAutomations[archetype], ...stepAutomations] as AutomationRun[];
    }

    if (isIosPlatform(platform)) {
        return [...iosStepAutomations[archetype], ...stepAutomations] as AutomationRun[];
    }

    return stepAutomations as AutomationRun[];
};
